import {
    FormControlLabel,
    Radio,
    RadioGroup,
} from '@material-ui/core';
import { useEffect, useRef, useState } from 'react';
import * as yup from 'yup';
import en from '../../../../lang/en-us';
import AddNominee, { camsRelations, kfinRelations } from './addNominee';
import NomineeCard from './NomineeCard';
import OptionalDetailsAccordion, { OptionalDetailsAccordionRef } from './OptionalDetailsAccordion';
import OptOutModal from './OptOutModal';
import {
    recalculateShares,
    handleShareChangeLogic,
    handleShareBlurLogic,
} from './nomineeShareUtils';
import { SubmitButtonWithLoader } from '../../../../lib/Button';
import { NoteModals } from '../../../../lib/NoteModals';
import { sendPhoneOTP } from '../../../../redux-store/actions/nct';
import { connect } from 'react-redux';
import { RootStateType } from '../../../../redux-store/reducers';
import {
    getDate,
    getDateString,
    UPDATE_NOMINEE,
    OTP_ACTIONS,
    nomineePanRegex,
    _minDateForCalender,
    emailRegex,
    mobileRegex,
    EIGHTEEN_YEARS_IN_MILLISECONDS,
} from '../../../../utils/constants';
import { ActionHeader, FaqLink } from '..';
import { CheckBoxField } from '../../../../lib/CheckBoxField';
import { KYCCheck } from '../../../common/KYCCheck';
import { RTA_CODES } from '../../commercial/emandate/types';
import { checkRequiredFields, getPinCode } from './types';

const {
    dashboard,
    dbCommercial,
    signUp: { form },
    common,
} = en;

export interface InitialState {
    relation: string;
    name: string;
    dob: Date | string;
    share: number | null;
    gender: string;
    pan: string;
    address1: string;
    address2: string;
    address3?: string;
    pincode: number | string;
    city: string;
    state: string;
    country: string;
    guardianName: string;
    guardianPan: string;
    guardianAddress: string;
    guardianDob: Date | string;
    guardianRelation?: string;
    isEditable?: boolean;
    isNewlyAdded?: boolean;
    showDetails?: boolean;
    editShare?: boolean;
    id?: string;
    isEdited?: boolean;
    isMinor?: boolean;
}
export interface NOMINEEINFO extends InitialState {
    initialState?: InitialState;
    docName?: string;
    docValue?: string;
    email?: string;
    mobile?: string;
    addressSameAsFH?: 'Y' | 'N';
    isShareTouched?: boolean;
    isMinor?: boolean;
}
export interface RELATIONS {
    label: string;
    value: string;
}
export const relationShips = [
    {
        label: 'Mother',
        value: 'mother',
    },
    {
        label: 'Son',
        value: 'son',
    },
    {
        label: 'Daughter',
        value: 'daughter',
    },
    {
        label: 'Father',
        value: 'father',
    },
    {
        label: 'Spouse',
        value: 'spouse',
    },
    {
        label: 'Sister',
        value: 'sister',
    },
    {
        label: 'Others',
        value: 'others',
    },
];

export const initialValues: NOMINEEINFO = {
    relation: dashboard.UpdateNominee.selectRelation,
    name: '',
    dob: '',
    share: null,
    gender: '',
    pan: '',
    address1: '',
    address2: '',
    pincode: '',
    city: '',
    addressSameAsFH: 'N',
    state: '',
    country: '',
    docName: '',
    docValue: '',
    email: '',
    mobile: '',
    guardianName: '',
    guardianPan: '',
    guardianAddress: '',
    guardianDob: '',
    guardianRelation: '',
    isMinor: false,
    isShareTouched: false,
};

export const isMinorNominee = (dob: any): boolean => {
    if (!dob) return false;
    return new Date().getTime() - new Date(dob).getTime() < EIGHTEEN_YEARS_IN_MILLISECONDS;
};

export const validationSchemaNominee = yup.object({
    relation: yup.string().required('Relation is required'),
    name: yup
        .string()
        .required('Name is required')
        .test('len', 'Name is Required', (val) => val?.trim().length !== 0),
    dob: yup.date().nullable().notRequired(),
    pan: yup.string().matches(nomineePanRegex, form.pan.validations.individualPan),
    address1: yup
        .string()
        .notRequired()
        .test('len', common.spaceNotAllowed, (val) => !val || val?.trim().length !== 0),
    address2: yup
        .string()
        .notRequired()
        .test('len', common.spaceNotAllowed, (val) => !val || val?.trim().length !== 0),
    city: yup
        .string()
        .notRequired()
        .test('len', common.spaceNotAllowed, (val) => !val || val?.trim().length !== 0)
        .max(50, common.cityMaxLength),
    state: yup
        .string()
        .notRequired()
        .test('len', common.spaceNotAllowed, (val) => !val || val?.trim().length !== 0)
        .max(50, common.stateMaxLength),
    pincode: yup
        .string()
        .notRequired()
        .test('pincode-length', 'Pincode should be of 6 digits', (val) => !val || val.length === 6),
    guardianPan: yup.string().notRequired().test(
        'valid-pan',
        form.pan.validations.individualPan,
        (val) => !val || val.trim() === '' || nomineePanRegex.test(val)
    ),
    guardianName: yup.string().notRequired().test(
        'valid-name',
        'Guardian name must be at least 4 characters',
        (val) => !val || val.trim() === '' || val.trim().length >= 4
    ),
    guardianDob: yup.string().when('dob', {
        is: isMinorNominee,
        then: yup.string().required('Guardian date of birth is required'),
        otherwise: yup.string().notRequired(),
    }),
});

function UpdateNomineeEdit(props: any) {
    const { nomineesData = {} } = props?.location?.state || {};
    const { nominees: data = [], investorName = '', address = null, rtaName = '', nomineeOptOut = '' } = nomineesData || {};
    const [nominees, setNominees] = useState<NOMINEEINFO[]>([]);
    const [noNomineeFlag, setNoNomineeFlag] = useState(nomineeOptOut === 'Y');
    const [show, setShow] = useState<boolean>(false);
    const [deleteIndex, setDeleteIndex] = useState<any>('');
    const [loading, setLoading] = useState(false);
    const [showShareModal, setShowShareModal] = useState<{ show: boolean; text: string }>({
        show: false,
        text: '',
    });
    const [nomineeInSoa, setNomineeInSoa] = useState<'Y' | 'N'>('N');
    const [showOptOutModal, setShowOptOutModal] = useState(false);
    const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});
    const isKfin = rtaName === RTA_CODES.KFIN;
    const optionalDetailsRef = useRef<OptionalDetailsAccordionRef>(null);

    const mainCardFields = ['name', 'relation', 'share', 'dob'];

    const accordionFields = [
        'gender',
        'address1',
        'address2',
        'country',
        'city',
        'state',
        'pincode',
        'email',
        'mobile',
        'docName',
        'docValue',
        'guardianName',
        'guardianPan',
        'guardianDob',
    ];

    /**
     * Smooth scroll to element with fallback for browsers that don't support smooth scrolling
     */
    const smoothScrollToElement = (element: Element | null) => {
        if (!element) return;
        
        try {
            const elementRect = element.getBoundingClientRect();
            const absoluteElementTop = elementRect.top + window.pageYOffset;
            const middle = absoluteElementTop - (window.innerHeight / 2) + (elementRect.height / 2);
            
            if ('scrollBehavior' in document.documentElement.style) {
                window.scrollTo({
                    top: middle,
                    behavior: 'smooth'
                });
            } else {
                const startPosition = window.pageYOffset;
                const distance = middle - startPosition;
                const duration = 500;
                let startTime: number | null = null;

                const animation = (currentTime: number) => {
                    if (startTime === null) startTime = currentTime;
                    const timeElapsed = currentTime - startTime;
                    const progress = Math.min(timeElapsed / duration, 1);
                    
                    const ease = progress < 0.5 
                        ? 2 * progress * progress 
                        : 1 - Math.pow(-2 * progress + 2, 2) / 2;
                    
                    window.scrollTo(0, startPosition + distance * ease);
                    
                    if (timeElapsed < duration) {
                        requestAnimationFrame(animation);
                    }
                };
                
                requestAnimationFrame(animation);
            }
        } catch (e) {
            // Silently fail if scroll fails
        }
    };

    /**
     * Scroll to the first error field
     */
    const scrollToError = (nomineeIndex: number, fieldName: string) => {
        if (nomineeIndex === null || nomineeIndex === undefined || nomineeIndex < 0) return;
        if (!fieldName) return;

        const isAccordionField = accordionFields.includes(fieldName);

        if (isAccordionField) {
            optionalDetailsRef.current?.scrollToNomineeField(nomineeIndex, fieldName);
        } else {
            try {
                const fieldElement = document.querySelector(
                    `[data-nominee-index="${nomineeIndex}"][data-field-name="${fieldName}"]`
                );
                if (fieldElement) {
                    smoothScrollToElement(fieldElement);
                    setTimeout(() => {
                        const inputElement = fieldElement.querySelector('input, select');
                        if (inputElement) {
                            (inputElement as HTMLElement).focus();
                        }
                    }, 550);
                }
            } catch (e) {
                // Silently fail if DOM element not found
            }
        }
    };

    /**
     * Validate all nominees and return the first error found
     */
    const validateNominees = (): { nomineeIndex: number; fieldName: string; message: string } | null => {
        if (!nominees || nominees.length === 0) return null;

        const drivingLicenseRegex = /^(([A-Z]{2}[0-9]{2})( )|([A-Z]{2}-[0-9]{2})|([A-Z]{2}[0-9]{2})())((19|20|[0-9]{2})[0-9][0-9])([0-9]{7}|[0-9]{8})$/;
        const aadharRegex = /^\d{4}$/;
        const passportNumberRegex = /^[A-Z]{1}[0-9]{7}$/;
        
        const errors: { [key: string]: string } = {};
        let firstError: { nomineeIndex: number; fieldName: string; message: string } | null = null;
        
        for (let i = 0; i < nominees.length; i++) {
            const nominee = nominees[i];
            if (!nominee) continue;

            if (!nominee.name || nominee.name.trim() === '') {
                const err = { nomineeIndex: i, fieldName: 'name', message: `Please enter name for nominee ${i + 1}` };
                errors[`${i}-name`] = err.message;
                if (!firstError) firstError = err;
            } else if (nominee.name.trim().length < 3) {
                const err = { nomineeIndex: i, fieldName: 'name', message: `Invalid nominee name. Please enter a valid name for nominee ${i + 1}` };
                errors[`${i}-name`] = err.message;
                if (!firstError) firstError = err;
            } else if (investorName && nominee.name.trim().toLowerCase() === investorName.trim().toLowerCase()) {
                const err = { nomineeIndex: i, fieldName: 'name', message: `Nominee name can not be investor name` };
                errors[`${i}-name`] = err.message;
                if (!firstError) firstError = err;
            }
            
            if (!nominee.relation || nominee.relation === dashboard.UpdateNominee.selectRelation) {
                const err = { nomineeIndex: i, fieldName: 'relation', message: `Please select relationship for nominee ${i + 1}` };
                errors[`${i}-relation`] = err.message;
                if (!firstError) firstError = err;
            }
            
            if (!nominee.share || Number(nominee.share) <= 0) {
                const err = { nomineeIndex: i, fieldName: 'share', message: `Please enter share percentage for nominee ${i + 1}` };
                errors[`${i}-share`] = err.message;
                if (!firstError) firstError = err;
            }

            if (nominee.isMinor && !nominee.dob) {
                const err = { nomineeIndex: i, fieldName: 'dob', message: `Please enter date of birth for minor nominee ${i + 1}` };
                errors[`${i}-dob`] = err.message;
                if (!firstError) firstError = err;
            }

            if (nominee.email && nominee.email.trim() !== '') {
                if (!emailRegex.test(nominee.email)) {
                    const err = { nomineeIndex: i, fieldName: 'email', message: `Please enter a valid email` };
                    errors[`${i}-email`] = err.message;
                    if (!firstError) firstError = err;
                } else if (!nominee.isMinor) {
                    // Check for duplicate email across major nominees
                    for (let j = 0; j < i; j++) {
                        const otherEmail = nominees[j].email;
                        if (!nominees[j].isMinor && otherEmail && otherEmail.trim() !== '') {
                            if (nominee.email.trim().toLowerCase() === otherEmail.trim().toLowerCase()) {
                                const err = { nomineeIndex: i, fieldName: 'email', message: `Nominee email should not be same as other nominee emails` };
                                errors[`${i}-email`] = err.message;
                                if (!firstError) firstError = err;
                                break;
                            }
                        }
                    }
                }
            }

            if (nominee.mobile && nominee.mobile.trim() !== '') {
                if (!mobileRegex.test(nominee.mobile)) {
                    const err = { nomineeIndex: i, fieldName: 'mobile', message: `Please enter a valid mobile number` };
                    errors[`${i}-mobile`] = err.message;
                    if (!firstError) firstError = err;
                } else if (!nominee.isMinor) {
                    // Check for duplicate mobile across major nominees
                    for (let j = 0; j < i; j++) {
                        const otherMobile = nominees[j].mobile;
                        if (!nominees[j].isMinor && otherMobile && otherMobile.trim() !== '') {
                            if (nominee.mobile.trim() === otherMobile.trim()) {
                                const err = { nomineeIndex: i, fieldName: 'mobile', message: `Nominee mobile should not be same as other nominee mobiles` };
                                errors[`${i}-mobile`] = err.message;
                                if (!firstError) firstError = err;
                                break;
                            }
                        }
                    }
                }
            }

            if (nominee.pincode && String(nominee.pincode).trim() !== '') {
                const pincodeStr = String(nominee.pincode).trim();
                const isIndia = !nominee.country || nominee.country === 'India' || nominee.country === '';
                
                if (isIndia) {
                    if (!/^\d{6}$/.test(pincodeStr)) {
                        const err = { nomineeIndex: i, fieldName: 'pincode', message: `Please enter a valid 6-digit pincode` };
                        errors[`${i}-pincode`] = err.message;
                        if (!firstError) firstError = err;
                    }
                } else {
                    if (!/^[A-Za-z0-9]{4,20}$/.test(pincodeStr)) {
                        const err = { nomineeIndex: i, fieldName: 'pincode', message: `Please enter a valid pincode` };
                        errors[`${i}-pincode`] = err.message;
                        if (!firstError) firstError = err;
                    }
                }
            }

            // If ID Number is entered but ID Type is not selected
            if (nominee.docValue && nominee.docValue.trim() !== '' && 
                (!nominee.docName || nominee.docName === dashboard.UpdateNominee.nomineeFields.docName)) {
                const err = { nomineeIndex: i, fieldName: 'docName', message: `Please select ID Type` };
                errors[`${i}-docName`] = err.message;
                if (!firstError) firstError = err;
            }

            if (nominee.docName && nominee.docName !== dashboard.UpdateNominee.nomineeFields.docName) {
                if (!nominee.docValue || nominee.docValue.trim() === '') {
                    const err = { nomineeIndex: i, fieldName: 'docValue', message: `Please enter ID Number` };
                    errors[`${i}-docValue`] = err.message;
                    if (!firstError) firstError = err;
                } else {
                    const docValue = nominee.docValue.trim();
                    
                    if (nominee.docName === 'A') {
                        if (!aadharRegex.test(docValue)) {
                            const err = { nomineeIndex: i, fieldName: 'docValue', message: `Please enter last 4 digits of Aadhaar` };
                            errors[`${i}-docValue`] = err.message;
                            if (!firstError) firstError = err;
                        } else if (docValue === '0000') {
                            const err = { nomineeIndex: i, fieldName: 'docValue', message: `Please enter a valid Aadhaar number` };
                            errors[`${i}-docValue`] = err.message;
                            if (!firstError) firstError = err;
                        } else {
                            // Check for duplicate Aadhaar across nominees
                            for (let j = 0; j < i; j++) {
                                const otherDocValue = nominees[j].docValue;
                                if (nominees[j].docName === 'A' && otherDocValue) {
                                    if (docValue === otherDocValue.trim()) {
                                        const err = { nomineeIndex: i, fieldName: 'docValue', message: `Nominee Aadhaar should not be same as other nominee Aadhaar` };
                                        errors[`${i}-docValue`] = err.message;
                                        if (!firstError) firstError = err;
                                        break;
                                    }
                                }
                            }
                        }
                    } else if (nominee.docName === 'P') {
                        if (!nomineePanRegex.test(docValue)) {
                            const err = { nomineeIndex: i, fieldName: 'docValue', message: `Please enter a valid PAN` };
                            errors[`${i}-docValue`] = err.message;
                            if (!firstError) firstError = err;
                        } else if (props.pan && docValue.toUpperCase() === props.pan.toUpperCase()) {
                            const err = { nomineeIndex: i, fieldName: 'docValue', message: `Nominee PAN should not be the investor PAN` };
                            errors[`${i}-docValue`] = err.message;
                            if (!firstError) firstError = err;
                        } else {
                            // Check for duplicate PAN across nominees
                            for (let j = 0; j < i; j++) {
                                const otherDocValue = nominees[j].docValue;
                                if (nominees[j].docName === 'P' && otherDocValue) {
                                    if (docValue.toUpperCase() === otherDocValue.trim().toUpperCase()) {
                                        const err = { nomineeIndex: i, fieldName: 'docValue', message: `Nominee PAN should not be same as other nominee PANs` };
                                        errors[`${i}-docValue`] = err.message;
                                        if (!firstError) firstError = err;
                                        break;
                                    }
                                }
                            }
                        }
                    } else if (nominee.docName === 'D') {
                        if (!drivingLicenseRegex.test(docValue)) {
                            const err = { nomineeIndex: i, fieldName: 'docValue', message: `Please enter a valid driving license` };
                            errors[`${i}-docValue`] = err.message;
                            if (!firstError) firstError = err;
                        } else {
                            // Check for duplicate Driving License across nominees
                            for (let j = 0; j < i; j++) {
                                const otherDocValue = nominees[j].docValue;
                                if (nominees[j].docName === 'D' && otherDocValue) {
                                    if (docValue.toUpperCase() === otherDocValue.trim().toUpperCase()) {
                                        const err = { nomineeIndex: i, fieldName: 'docValue', message: `Nominee Driving License should not be same as other nominee Driving License` };
                                        errors[`${i}-docValue`] = err.message;
                                        if (!firstError) firstError = err;
                                        break;
                                    }
                                }
                            }
                        }
                    } else if (nominee.docName === 'I') {
                        if (!passportNumberRegex.test(docValue)) {
                            const err = { nomineeIndex: i, fieldName: 'docValue', message: `Please enter a valid passport number` };
                            errors[`${i}-docValue`] = err.message;
                            if (!firstError) firstError = err;
                        } else {
                            // Check for duplicate Passport across nominees
                            for (let j = 0; j < i; j++) {
                                const otherDocValue = nominees[j].docValue;
                                if (nominees[j].docName === 'I' && otherDocValue) {
                                    if (docValue.toUpperCase() === otherDocValue.trim().toUpperCase()) {
                                        const err = { nomineeIndex: i, fieldName: 'docValue', message: `Nominee Passport should not be same as other nominee Passport` };
                                        errors[`${i}-docValue`] = err.message;
                                        if (!firstError) firstError = err;
                                        break;
                                    }
                                }
                            }
                        }
                    }
                }
            }

            if (nominee.isMinor) {
                if (!nominee.dob) {
                    const err = { nomineeIndex: i, fieldName: 'dob', message: `Please enter date of birth for minor nominee` };
                    errors[`${i}-dob`] = err.message;
                    if (!firstError) firstError = err;
                } else if (!isMinorNominee(nominee.dob)) {
                    const err = { nomineeIndex: i, fieldName: 'dob', message: `DOB indicates nominee is not a minor` };
                    errors[`${i}-dob`] = err.message;
                    if (!firstError) firstError = err;
                }

                // Guardian Name is optional, validate only if entered
                if (nominee.guardianName && nominee.guardianName.trim() !== '') {
                    if (nominee.guardianName.trim().length < 4) {
                        const err = { nomineeIndex: i, fieldName: 'guardianName', message: `Invalid guardian name. Please enter a valid name` };
                        errors[`${i}-guardianName`] = err.message;
                        if (!firstError) firstError = err;
                    } else if (nominee.name && nominee.guardianName.trim().toUpperCase() === nominee.name.trim().toUpperCase()) {
                        const err = { nomineeIndex: i, fieldName: 'guardianName', message: `Guardian name should not be same as nominee name` };
                        errors[`${i}-guardianName`] = err.message;
                        if (!firstError) firstError = err;
                    }
                }

                // Guardian PAN is optional, validate only if entered
                if (nominee.guardianPan && nominee.guardianPan.trim() !== '') {
                    if (!nomineePanRegex.test(nominee.guardianPan.trim())) {
                        const err = { nomineeIndex: i, fieldName: 'guardianPan', message: `Please enter a valid guardian PAN` };
                        errors[`${i}-guardianPan`] = err.message;
                        if (!firstError) firstError = err;
                    }
                }
            } else if (nominee.dob && isMinorNominee(nominee.dob)) {
                // DOB indicates minor but checkbox not selected
                const err = { nomineeIndex: i, fieldName: 'isMinor', message: `Nominee ${i + 1}: DOB indicates nominee is below 18 years. Please select 'This nominee is below 18 years of age'` };
                errors[`${i}-isMinor`] = err.message;
                if (!firstError) firstError = err;
            }
        }
        
        setFieldErrors(errors);
        
        return firstError;
    };

    const handleShareChange = (newValue: string, index: number, currentNominees: NOMINEEINFO[]) => {
        return handleShareChangeLogic(newValue, index, currentNominees);
    };

    const handleShareBlur = (index: number) => {
        setNominees((prevNominees) => {
            const result = handleShareBlurLogic(index, prevNominees);
            return result !== null ? result : prevNominees;
        });
    };

    const relations = isKfin ? kfinRelations : camsRelations;
    useEffect(() => {
        if (data && data.length) {
            const mappedNominees = data.slice(0, 3).map((_ele: any) => {
                if (!_ele) return null;
                const percentValue = _ele.percent;
                const hasValidPercent = percentValue !== '' && percentValue !== null && percentValue !== undefined;
                let nomineeState = {
                    relation: relations.includes(_ele?.relation)
                        ? _ele.relation
                        : dashboard.UpdateNominee.selectRelation,
                    name: _ele.name || '',
                    dob: getDate(_ele.dob),
                    share: hasValidPercent ? percentValue : null,
                    isShareTouched: false,
                    pan: _ele.pan || '',
                    gender: (_ele.gender || '').toLowerCase(),
                    ..._ele.address,
                    addressSameAsFH: _ele.addressSameAsFH || 'Y',
                    address1: address?.line1 || '',
                    address2: address?.line2 || '',
                    pincode: address?.pincode || '',
                    city: address?.city || '',
                    state: address?.state || '',
                    country: address?.country || '',
                    docName: _ele?.docName || '',
                    docValue: _ele?.docValue || '',
                    email: _ele?.email || '',
                    mobile: _ele?.mobile || '',
                    guardianName: _ele?.guardian?.name || '',
                    guardianPan: _ele?.guardian?.pan || '',
                    guardianAddress: _ele?.guardian?.address || '',
                    guardianDob: _ele?.guardian?.dob || '',
                    guardianRelation: _ele?.guardian?.relation || '',
                    isMinor: isMinorNominee(getDate(_ele.dob)),
                    isEditable: false,
                    showDetails: false,
                    editShare: false,
                    isNewlyAdded: false,
                    id: _ele.id,
                    isEdited: false,
                };
                return { ...nomineeState, initialState: nomineeState };
            }).filter(Boolean);
            let hasAnyEmptyShare = false;
            for (let i = 0; i < mappedNominees.length; i++) {
                if (mappedNominees[i] && mappedNominees[i].share === null) {
                    hasAnyEmptyShare = true;
                    break;
                }
            }
            if (hasAnyEmptyShare) {
                const nomineesWithShares = recalculateShares(mappedNominees);
                setNominees(nomineesWithShares);
            } else {
                setNominees(mappedNominees);
            }
        } else {
            setNominees([
                {
                    ...initialValues,
                    share: 100,
                    isShareTouched: false,
                    isEditable: false,
                    showDetails: false,
                    editShare: false,
                    isNewlyAdded: true,
                    isEdited: false,
                },
            ]);
        }
    }, []);

    const handleAddNomiee = () => {
        const newNominee: NOMINEEINFO = { 
            ...initialValues, 
            share: null,
            isShareTouched: false,
            isEditable: true, 
            isNewlyAdded: true 
        };
        
        const allNominees = [...nominees, newNominee];
        const updatedNominees = recalculateShares(allNominees);
        
        setNominees(updatedNominees.map(nom => ({ ...nom, isEdited: true })));
    };

    const cancelFormSubmission = (index: number, id: string) => {
        const nominee = nominees[index];
        const { isNewlyAdded } = nominee;
        if (isNewlyAdded) {
            setNominees(nominees.filter((_ele: NOMINEEINFO, ind: number) => index !== ind));
            return;
        }
        setNominees((prevState: NOMINEEINFO[]) => {
            const nominees = [...prevState];
            const nominee = nominees[index];
            nominees[index] = { ...nominee, ...nominee.initialState, isEditable: false };
            return nominees;
        });
    };

    const resetForm = (index: number) => {
        const nominee = [...nominees];
        nominee[index] = { ...initialValues, isEditable: true };
        setNominees(nominee);
    };

    const handleNomineeChange = (key: any, value: any, index: any) => {
        if (key === 'share') {
            setNominees((prevNominees) => {
                const updatedNominees = handleShareChange(value, index, prevNominees);
                return updatedNominees.map((nom, idx) => 
                    idx === index ? { ...nom, isEdited: true } : nom
                );
            });
            return;
        }
        
        setNominees((prevNominees) => {
            const updatedNominees = [...prevNominees];
            updatedNominees[index] = { ...updatedNominees[index], [key]: value };
            return updatedNominees;
        });
    };

    const handleDeleteNominee = (ind: number) => {
        const remainingNominees = nominees.filter((_ele: NOMINEEINFO, index: number) => index !== ind);
        const updatedNominees = recalculateShares(remainingNominees);
        setNominees(updatedNominees.map(nom => ({ ...nom, isEdited: true })));
    };

    const handleSubmit = () => {
        try {
            setShow(false);
            handleDeleteNominee(deleteIndex);
        } catch (err: any) {
            alert(err.message);
        }
    };

    const handleUpdateNominee = async () => {
        const isNomineeUpdated = nominees.find((_ele: NOMINEEINFO) => _ele.isEdited === true);
        const hasNewlyAdded = nominees.find((_ele: NOMINEEINFO) => _ele.isNewlyAdded === true);
        if (!isNomineeUpdated && !hasNewlyAdded && !noNomineeFlag) {
            alert('Please make the necessary changes to proceed with your request');
            return;
        }

        if (!noNomineeFlag) {
            const validationError = validateNominees();
            if (validationError) {
                scrollToError(validationError.nomineeIndex, validationError.fieldName);
                alert(validationError.message);
                return;
            }
        }

        try {
            setLoading(true);
            let nomineePayload = null;
            if (!noNomineeFlag) {
                const totalShare = nominees
                    .map((_ele: NOMINEEINFO) => _ele.share)
                    .reduce((sum: number = 0, current) => sum + Number(current), 0);
                if (totalShare > 100) {
                    scrollToError(0, 'share');
                    throw Error(dashboard.UpdateNominee.NoteModals.greaterModal);
                }
                if (totalShare < 100) {
                    scrollToError(0, 'share');
                    throw Error(dashboard.UpdateNominee.NoteModals.lessModal);
                }
                if (nominees.length) {
                    let names: string[] = [];
                    let error = false;
                    let duplicateIndex = 0;
                    nominees.some((nominee, idx) => {
                        const normalizedName = nominee.name.trim().toLowerCase();
                        if (names.includes(normalizedName)) {
                            error = true;
                            duplicateIndex = idx;
                        }
                        names = [...names, normalizedName];
                        return false;
                    });
                    if (error) {
                        scrollToError(duplicateIndex, 'name');
                        throw Error('Nominees name should be different from each other');
                    }
                }
                // if (
                //     !nominees[0].name ||
                //     !nominees[0].dob ||
                //     !nominees[0].share ||
                //     !nominees[0].relation ||
                //     !nominees[0].gender
                // ) {
                //     throw Error('Nominee Details cannot be empty');
                // }
                nomineePayload = nominees.map((_ele: NOMINEEINFO, ind: number) => ({
                    nominee: ind + 1,
                    name: _ele.name,
                    percent: _ele.share,
                    gender: _ele.gender,
                    dob: _ele.isMinor ? getDateString(_ele.dob) : '',
                    pan: _ele.pan,
                    address1:
                        _ele.addressSameAsFH === 'Y'
                            ? isKfin
                                ? address?.line1
                                : ''
                            : _ele.address1,
                    address2:
                        _ele.addressSameAsFH === 'Y'
                            ? isKfin
                                ? address?.line2
                                : ''
                            : _ele.address2,
                    email: _ele.email,
                    mobile: _ele.mobile,
                    address3: _ele.country !== 'India' && isKfin ? _ele?.pincode : '',
                    city: _ele.addressSameAsFH === 'Y' ? (isKfin ? address?.city : '') : _ele?.city,
                    state:
                        _ele.addressSameAsFH === 'Y' ? (isKfin ? address?.state : '') : _ele?.state,
                    pincode: getPinCode(_ele, isKfin, address),
                    country:
                        _ele.addressSameAsFH === 'Y'
                            ? isKfin
                                ? address?.country
                                : ''
                            : _ele?.country,
                    relation: _ele?.relation,
                    docName: _ele?.docName,
                    docValue: _ele?.docValue,
                    addressSameAsFH: isKfin ? 'N' : _ele?.addressSameAsFH,
                    // no incapacitation feature for now
                    inCapacitation: false,
                    guardian: {
                        name: _ele.guardianName,
                        pan: _ele.guardianPan,
                        pekrn: '',
                        dob: getDateString(_ele.guardianDob),
                        relation: _ele.guardianRelation || '',
                    },
                }));
                try {
                    if (isKfin) {
                        nominees.forEach((nominee) => {
                            checkRequiredFields(nominee);
                        });
                    }
                } catch (e: any) {
                    alert(e.message);
                    return;
                }
            }
            const { pan = '', pekrn = '', mobile = '' } = props;
            let res = await props.sendPhoneOTP({
                mobile,
                pan,
                pekrn,
                action: OTP_ACTIONS.updateNominee,
            });
            const nomineeApiPayload = {
                folio: nomineesData.id,
                nomineeInSoa,
                amc: nomineesData.amc,
                noNominee: noNomineeFlag ? 'Y' : 'N',
                nominees: noNomineeFlag ? [] : nomineePayload,
                eNomType: 'F',
                eSignFlag: '',
                eNomMobileStatus: 'D',
                eNomDocId: '',
            };
            props.history.replace('update-nominee-edit-otp', {
                mobile: props.mobile,
                pan: props.pan,
                data: [nomineeApiPayload],
                ...(res || {}).body,
            });
        } catch (e: any) {
            setShowShareModal({ show: true, text: e.message });
            // alert(e.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="curved-bg">
            <KYCCheck
                allowRegistration={{
                    isin: nomineesData.isin,
                    amc: nomineesData.amc,
                }}
            />
            <ActionHeader header={dashboard.UpdateNominee.UpdateNomineeHeading} />
            <div className="curved-bg-content curved-bg-table-outer update-nominee-screen">
                <div className="curved-bg-content-table">
                    {/* <h5> {dashboard.UpdateNominee.UpdateNomineeInfo}</h5> */}
                    <div className="curved-table-content update-nominee">
                        {/* Nominee Cards */}
                        {!noNomineeFlag &&
                            nominees.map((_ele: NOMINEEINFO, index: number) => (
                                <NomineeCard
                                    key={_ele.id || index}
                                    index={index}
                                    nominee={_ele}
                                    isKfin={isKfin}
                                    totalNominees={nominees.length}
                                    totalSharePercent={nominees.reduce(
                                        (sum, n) => sum + Number(n.share || 0),
                                        0,
                                    )}
                                    handleNomineeChange={(
                                        key: string,
                                        value: any,
                                        ind: number,
                                    ) => {
                                        if (fieldErrors[`${ind}-${key}`]) {
                                            setFieldErrors(prev => {
                                                const newErrors = { ...prev };
                                                delete newErrors[`${ind}-${key}`];
                                                return newErrors;
                                            });
                                        }
                                        if (key === 'share') {
                                            handleNomineeChange(key, value, ind);
                                        } else if (key !== 'isEdited') {
                                            setNominees((prevNominees) => {
                                                const updatedNominees = [...prevNominees];
                                                updatedNominees[ind] = { 
                                                    ...updatedNominees[ind], 
                                                    [key]: value, 
                                                    isEdited: true 
                                                };
                                                return updatedNominees;
                                            });
                                        } else {
                                            handleNomineeChange(key, value, ind);
                                        }
                                    }}
                                    handleRemoveNominee={(ind: number) => {
                                        setShow(true);
                                        setDeleteIndex(ind);
                                    }}
                                    onShareBlur={handleShareBlur}
                                    errors={{
                                        name: fieldErrors[`${index}-name`] || '',
                                        relation: fieldErrors[`${index}-relation`] || '',
                                        share: fieldErrors[`${index}-share`] || '',
                                        dob: fieldErrors[`${index}-dob`] || '',
                                    }}
                                />
                            ))}

                        {/* Add Nominee Button */}
                        {nominees.length < 3 && !noNomineeFlag && (
                            <div className="add-nominee-btn add-nominee-btn-dashed">
                                <a onClick={() => handleAddNomiee()}>
                                    + {dashboard.UpdateNominee.addNominee}
                                </a>
                            </div>
                        )}

                        {/* Optional Details Accordion */}
                        {!noNomineeFlag && nominees.length > 0 && (
                            <OptionalDetailsAccordion
                                ref={optionalDetailsRef}
                                nominees={nominees}
                                handleNomineeChange={(
                                    key: string,
                                    value: any,
                                    ind: number,
                                ) => {
                                    handleNomineeChange(key, value, ind);
                                    if (key !== 'isEdited') {
                                        handleNomineeChange('isEdited', true, ind);
                                    }
                                    if (fieldErrors[`${ind}-${key}`]) {
                                        setFieldErrors(prev => {
                                            const newErrors = { ...prev };
                                            delete newErrors[`${ind}-${key}`];
                                            return newErrors;
                                        });
                                    }
                                }}
                                primaryHolderAddress={address}
                                fieldErrors={fieldErrors}
                            />
                        )}

                        {/* I do not wish to nominate checkbox */}
                        <div className="no-nominee-checkbox">
                            <CheckBoxField
                                onChange={(e) => {
                                    if (e.target.checked) {
                                        setShowOptOutModal(true);
                                    } else {
                                        setNoNomineeFlag(false);
                                    }
                                }}
                                value={noNomineeFlag}
                                label={dbCommercial.profileDetails.nominees.noNominee}
                            />
                        </div>

                        {/* Opt-Out Confirmation Modal */}
                        <OptOutModal
                            showModal={showOptOutModal}
                            onConfirm={() => {
                                setNoNomineeFlag(true);
                                setShowOptOutModal(false);
                            }}
                            onCancel={() => {
                                setShowOptOutModal(false);
                            }}
                        />

                        {/* SOA Radio Options */}
                        {!noNomineeFlag && (
                            <div className="nominee-in-soa-checkbox">
                                <p>{dashboard.UpdateNominee.includeInSOA}</p>
                                <RadioGroup
                                    name="nomineeInSoa"
                                    value={nomineeInSoa}
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        marginTop: '10px',
                                    }}
                                    onChange={(e) => setNomineeInSoa(e.target.value as 'Y' | 'N')}
                                >
                                    <FormControlLabel
                                        value="Y"
                                        control={<Radio />}
                                        label="Name of the Nominee(s)"
                                    />
                                    <FormControlLabel
                                        value="N"
                                        control={<Radio />}
                                        label="Whether nomination given: Yes / No (not name of the nominee)"
                                    />
                                </RadioGroup>
                            </div>
                        )}

                        <FaqLink section={UPDATE_NOMINEE} history={props.history} />
                        <div className="curved-bg-content-inner-bottom">
                            <SubmitButtonWithLoader
                                type="submit"
                                loading={loading}
                                labelWhileLoading={'Submitting'}
                                className="customBtn"
                                label={'Submit'}
                                onClick={handleUpdateNominee}
                            />
                        </div>
                        <NoteModals
                            showModal={show}
                            info={dashboard.UpdateNominee.NoteModals.deleteNominee}
                            proceedBtn={dashboard.noteModals.otpModal.proceedText}
                            cancelBtn={dashboard.noteModals.otpModal.cancelText}
                            onProceed={handleSubmit}
                            onCancel={() => setShow(false)}
                        />
                        <NoteModals
                            showModal={showShareModal.show}
                            info={showShareModal.text}
                            proceedBtn={dashboard.UpdateNominee.NoteModals.okay}
                            extraClass="share-modal"
                            onProceed={() =>
                                setShowShareModal({
                                    show: false,
                                    text: '',
                                })
                            }
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default connect(
    ({ authReducer, portfolio }: RootStateType) => ({ ...authReducer, ...portfolio }),
    { sendPhoneOTP },
)(UpdateNomineeEdit);
